/**
 * The dedicated audience-agent chat hook — the audience twin of `useDashboardChat`.
 *
 * Same turn-scoped SSE mechanics (lazy getOrCreate, optimistic user_message, stop-turn abort, aborted-
 * prompt restore) but bound to the `audienceChat` oRPC mount and keyed by `audienceId` instead of a
 * dashboard. The server rebuilds the audience context entirely from the freshly-read row (title,
 * status, warehouse, definition, last count), so no client-supplied context is trusted. The ONE
 * exception is `openDraft` (ACT2-623): the unsaved proposal on the page, echoed back so the agent keeps
 * its own edit in view across turns. The server re-parses it and drops it when it is malformed.
 * On every turn boundary it invalidates the audience read
 * queries (`get` + `list` + the derived count) so a definition or count the agent changed mid-turn
 * refreshes in the left-hand pane.
 */
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { InteractiveAnswer, PendingBlock, SSEMessage } from '@/_shared/types';

import { derivePendingBlock } from '../../chat/hooks/sse-reducer';
import { useEndedStreamHistory } from '../../chat/hooks/useEndedStreamHistory';
import { useInteractiveBlockResolver } from '../../chat/hooks/useInteractiveBlockResolver';
import type { TurnRecoveryStatus } from '../../chat/hooks/useSSE';
import { useSSE } from '../../chat/hooks/useSSE';
import { useTurnStop } from '../../chat/hooks/useTurnStop';
import { filterOutTurn, isTurnTerminalInHistory } from '../../chat/lib/turn-slicing';

export interface UseAudienceChatOptions {
  /**
   * The agent PROPOSED a definition for a saved audience (ACT2-623). The proposal is never stored, so
   * this callback is the only thing that puts it on screen. The page holds it as its local draft, where
   * Discard and Save already know what to do with it.
   */
  onProposal?: (definition: unknown, definitionHash: string) => void;
  /**
   * The agent counted the open PROPOSAL (ACT2-623). Nothing recorded it, so this is the only way the
   * size bar learns the number the agent is quoting. `definitionHash` says which definition it
   * measured — the page drops a number that no longer describes what is on screen. `revision` is the
   * page's draft counter, used when the on-screen draft has no hash (a hand edit).
   */
  onProposalCount?: (count: {
    definitionHash: string;
    members: number;
    universe: number;
    percentage: number;
    revision?: number;
    /** The warehouse's own refusal of the statement this audience publishes, though both counts ran (ACT2-797). */
    membershipDriverMessage?: string;
  }) => void;
  /** The unsaved definition currently open on the page, sent with each message. Null when there is none. */
  openDraft?: unknown;
  /** The page's monotonic draft revision, echoed with `openDraft` so a count can match a hash-less edit. */
  openDraftRevision?: number;
}

export interface UseAudienceChatReturn {
  sessionId: string | null;
  messages: SSEMessage[];
  connected: boolean;
  /** Demo workspace only: this thread hit its LLM budget and the server will refuse further turns. */
  budgetExhausted: boolean;
  error: string | null;
  sendMessage: (content: string) => void;
  resetChat: () => void;
  recoveryStatus: TurnRecoveryStatus;
  retryRecovery: () => void;
  agentActive: boolean;
  stopping: boolean;
  stopStatusError: boolean;
  stopStalled: boolean;
  retryStopStatus: () => void;
  /** True once getOrCreate has resolved a session — messages can be sent. */
  sessionReady: boolean;
  stopTurn: () => void;
  /** True when the agent is active AND a turn ID has been confirmed — safe to show the Stop button. */
  canStop: boolean;
  /** A message to put back into the composer: one whose send failed. A stopped prompt stays in the transcript. */
  prefillValue: string | undefined;
  /** Bumps each time a new prefill should be applied. */
  prefillNonce: number;
  /** Interactive blocks (AI-411): the question/approval block currently gating the composer, if any. */
  pendingBlock: PendingBlock | null;
  /** Submit an answer for the active interactive block (reuses the session-generic answerInteractive RPC). */
  resolveBlock: (block: PendingBlock, answer: InteractiveAnswer) => void;
  /** True while the active block's answer is in flight — locks its controls. */
  blockSubmitting: boolean;
  /** True from the end of a turn until the refetch it triggered lands — see `settlingAfterTurn`. */
  settlingAfterTurn: boolean;
  /** Bumped when a block resolves so the composer refocuses once it re-enables (a11y). */
  composerFocusNonce: number;
}

export function useAudienceChat(audienceId: string | null, options?: UseAudienceChatOptions): UseAudienceChatReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentActive, setAgentActive] = useState(false);
  const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
  const [adoptedRuntimeTurn, setAdoptedRuntimeTurn] = useState<{ sessionId: string; turnId: string } | null>(null);
  const [prefillValue, setPrefillValue] = useState<string | undefined>(undefined);
  const [prefillNonce, setPrefillNonce] = useState(0);
  const queryClient = useQueryClient();
  const { slug } = useWorkspace();
  const orpc = useOrpc();
  const turnStop = useTurnStop({
    workspaceKey: slug,
    sessionId,
    activeTurnId,
    stopCall: (input) => orpc.audienceChat.stopTurn.call(input),
    // A stopped prompt stays in the transcript, marked "Stopped" (the abort marker). The composer is
    // never touched, so a draft typed during the turn stays put.
    onConfirmed: () => {
      setAgentActive(false);
      setActiveTurnId(null);
    },
  });
  const { noteTerminal, noteStreamEnded, stop, blockingTurnId } = turnStop;
  const initializedForSession = useRef<string | null>(null);

  // Latest-value refs so the SSE handler and `sendMessage` stay referentially stable — a new callback
  // identity on every keystroke of the draft would tear down and rebuild the event stream.
  const onProposalRef = useRef(options?.onProposal);
  const onProposalCountRef = useRef(options?.onProposalCount);
  const openDraftRef = useRef(options?.openDraft);
  const openDraftRevisionRef = useRef(options?.openDraftRevision);
  useEffect(() => {
    onProposalRef.current = options?.onProposal;
    onProposalCountRef.current = options?.onProposalCount;
    openDraftRef.current = options?.openDraft;
    openDraftRevisionRef.current = options?.openDraftRevision;
  });

  // Reset all state when the audience changes (render-phase pattern).
  const [prevAudienceId, setPrevAudienceId] = useState(audienceId);
  if (audienceId !== prevAudienceId) {
    setPrevAudienceId(audienceId);
    setSessionId(null);
    setAgentActive(false);
    setActiveTurnId(null);
    setAdoptedRuntimeTurn(null);
    setPrefillValue(undefined);
    setPrefillNonce(0);
  }

  const { data: sessionData, refetch: refetchSession } = useQuery(
    orpc.audienceChat.getOrCreate.queryOptions({
      input: audienceId ? { audienceId } : skipToken,
    }),
  );

  // Sync session ID from server data (render-phase pattern) — see useDashboardChat for why prev seeds
  // to null (adopting a cache hit on the first render otherwise strands the chat unready).
  const serverSessionId = sessionData?.session?.id ?? null;
  const [prevServerSessionId, setPrevServerSessionId] = useState<string | null>(null);
  if (serverSessionId !== prevServerSessionId) {
    setPrevServerSessionId(serverSessionId);
    if (serverSessionId && serverSessionId !== sessionId) {
      setSessionId(serverSessionId);
    }
  }

  /**
   * True from the moment a turn ends until the refetch it triggered has landed (ACT2-623 review #3).
   *
   * The page disables Discard and Save across this window: `agentActive` clears BEFORE the refetch
   * resolves, and a Save in between sends whatever title is in hand — which the agent's `rename_audience`
   * may already have moved. It is deliberately NOT `audience.get`'s raw `isFetching`: that also covers a
   * window-focus refetch and every unrelated invalidation, so an open draft lost its controls for a beat
   * whenever the tab regained focus.
   */
  const [settlingAfterTurn, setSettlingAfterTurn] = useState(false);

  const invalidateAudience = useCallback(() => {
    if (!audienceId) return;
    setSettlingAfterTurn(true);
    void queryClient
      .invalidateQueries({
        queryKey: orpc.audience.get.queryOptions({ input: { id: audienceId } }).queryKey,
      })
      .finally(() => setSettlingAfterTurn(false));
    // The sketch is the editor's FULL choice domain, fetched lazily when a value editor opens — so it
    // outlives the turn that changed it unless it is invalidated here too. `audience.get` carries the
    // derived rows and is the visible path today, which is why a stale sketch has not bitten yet; that
    // is a reason it is cheap to fix, not a reason to leave it.
    // Input key is `audienceId`, not `id` — see `rpc/procedures/audience.ts`'s `sketch` procedure.
    void queryClient.invalidateQueries({
      queryKey: orpc.audience.sketch.queryOptions({ input: { audienceId } }).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
    // `audience.rowsFor` is the editor rows for an open PROPOSAL. A probe that lands mid-turn fills
    // the sketch, but the proposal's cached rows were derived before that measurement — without this
    // the added rule stays bare text until an unrelated refetch. Input varies by definition, so
    // invalidate the procedure, not one key.
    void queryClient.invalidateQueries({ queryKey: orpc.audience.rowsFor.key() });
  }, [audienceId, queryClient, orpc]);

  const handleSSEEvent = useCallback(
    (eventType: string, data: Record<string, unknown>) => {
      if (eventType === 'turn_complete' || eventType === 'turn_aborted' || eventType === 'error') {
        const id =
          typeof data['turnId'] === 'string'
            ? data['turnId']
            : (activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId);
        if (id) noteTerminal(id);
      }
      if (eventType === 'turn_complete' || eventType === 'error') {
        setAgentActive(false);
        setActiveTurnId(null);
        invalidateAudience();
        // Refresh runtime and terminal history for every chat, including rollout recovery.
        void refetchSession();
      }
      if (eventType === 'audience_draft') {
        // Ephemeral by contract: nothing was stored, so this event is the whole delivery. A dropped
        // stream simply means no proposal appears, and the user can ask the agent to send it again.
        const definition = data['definition'];
        const definitionHash = data['definitionHash'];
        if (definition != null && typeof definitionHash === 'string') {
          onProposalRef.current?.(definition, definitionHash);
        }
      }
      if (eventType === 'audience_draft_count') {
        const definitionHash = data['definitionHash'];
        const members = data['members'];
        const universe = data['universe'];
        const percentage = data['percentage'];
        const revision = data['revision'];
        const membershipDriverMessage = data['membershipDriverMessage'];
        if (
          typeof definitionHash === 'string' &&
          typeof members === 'number' &&
          typeof universe === 'number' &&
          typeof percentage === 'number'
        ) {
          onProposalCountRef.current?.({
            definitionHash,
            members,
            universe,
            percentage,
            ...(typeof revision === 'number' ? { revision } : {}),
            ...(typeof membershipDriverMessage === 'string' ? { membershipDriverMessage } : {}),
          });
        }
      }
      if (eventType === 'turn_aborted') {
        setAgentActive(false);
        setActiveTurnId(null);
        invalidateAudience();
        void refetchSession();
      }
    },
    [invalidateAudience, refetchSession, activeTurnId, sessionData?.runtime?.activeTurn?.turnId, noteTerminal],
  );

  const handleSSEDisconnect = useCallback(() => {
    // intentionally empty — agentActive is managed by SSE events, not connectivity
  }, []);

  const sseOptions = useMemo(
    () => ({ onEvent: handleSSEEvent, onDisconnect: handleSSEDisconnect }),
    [handleSSEEvent, handleSSEDisconnect],
  );

  // Turn-scoped SSE: only connect when there's an active turn to follow. Audience chat sessions are
  // ordinary chat sessions, so they use the same per-turn events endpoint.
  const knownTurnId = activeTurnId ?? sessionData?.runtime?.activeTurn?.turnId ?? null;
  const streamUrl =
    sessionId && knownTurnId && slug
      ? `/api/w/${encodeURIComponent(slug)}/chats/${sessionId}/turns/${knownTurnId}/events`
      : null;
  const {
    messages,
    connected,
    error: sseError,
    ended: sseEnded,
    endedTurnId,
    recovered: streamHistoryRecovered,
    recoveryStatus,
    retryRecovery,
    recoverHistory,
    addMessage,
    removeLastUserMessage,
    initializeMessages,
    clearMessages,
    reconnect,
  } = useSSE(streamUrl, {
    ...sseOptions,
    scopeKey: sessionId,
    turnId: knownTurnId,
    recovery: {
      sessionId,
      persistedMessages: sessionData?.sseMessages,
      runtimeTurnId: sessionData?.runtime?.activeTurn?.turnId ?? null,
      refetchSession,
    },
  });

  useEffect(() => {
    if (sseEnded && knownTurnId) noteStreamEnded(knownTurnId);
  }, [sseEnded, knownTurnId, noteStreamEnded]);
  useEndedStreamHistory({
    sessionId,
    turnId: endedTurnId,
    ended: sseEnded && !streamHistoryRecovered,
    recoverHistory,
  });

  const reportedTurnId = sessionData?.runtime?.activeTurn?.turnId ?? null;
  if (
    sessionId &&
    sessionData?.session.id === sessionId &&
    reportedTurnId &&
    (adoptedRuntimeTurn?.sessionId !== sessionId || adoptedRuntimeTurn.turnId !== reportedTurnId) &&
    (!activeTurnId || activeTurnId === reportedTurnId)
  ) {
    setAdoptedRuntimeTurn({ sessionId, turnId: reportedTurnId });
    if (
      !isTurnTerminalInHistory(sessionData.sseMessages, reportedTurnId) &&
      !isTurnTerminalInHistory(messages, reportedTurnId)
    ) {
      setAgentActive(true);
      setActiveTurnId(reportedTurnId);
    }
  }

  // Interactive blocks (AI-411): the question/approval block gating the composer, derived from the SSE
  // message stream (the same selector the main chat uses). At most one is active per single-threaded turn.
  const pendingBlock = useMemo(() => derivePendingBlock(messages), [messages]);
  const { resolveBlock, submittingBlockId, composerFocusNonce } = useInteractiveBlockResolver({
    sessionId,
    addMessage,
    reconnect,
  });

  // Unlock after a final stream failure. Mirrors useDashboardChat.
  useEffect(() => {
    if (agentActive && activeTurnId && sseEnded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async stream failure unlocks the local turn
      setAgentActive(false);
      setActiveTurnId(null);
    }
  }, [agentActive, activeTurnId, sseEnded]);

  useEffect(() => {
    if (!sessionId) clearMessages();
  }, [sessionId, clearMessages]);

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });

  // Initialize from historical messages (once per session); filter out the active turn (SSE owns it).
  useEffect(() => {
    if (sessionId && sessionData?.sseMessages && initializedForSession.current !== sessionId) {
      initializedForSession.current = sessionId;
      const filtered = knownTurnId ? filterOutTurn(sessionData.sseMessages, knownTurnId) : sessionData.sseMessages;
      const currentSSE = messagesRef.current;
      if (currentSSE.length > 0) {
        initializeMessages([...filtered, ...currentSSE]);
      } else {
        initializeMessages(filtered);
      }
    }
  }, [sessionId, sessionData?.sseMessages, knownTurnId, initializeMessages]);

  const stopTurn = useCallback(() => {
    const id = knownTurnId ?? blockingTurnId;
    if (id) stop(id);
  }, [knownTurnId, blockingTurnId, stop]);

  const sendMessageMutation = useMutation(orpc.audienceChat.sendMessage.mutationOptions());

  const sendMessage = useCallback(
    (content: string) => {
      if (!sessionId || recoveryStatus !== null || turnStop.blocking) return;
      addMessage({ type: 'user_message', content });
      setAgentActive(true);
      // The proposal the page is showing, echoed back so the agent builds on its own edit rather than on
      // the stored rules it deliberately did not write.
      const openDraft = openDraftRef.current;
      const openDraftRevision = openDraftRevisionRef.current;
      const variables = {
        sessionId,
        content,
        ...(openDraft != null
          ? {
              draft: {
                definition: openDraft,
                ...(openDraftRevision !== undefined ? { revision: openDraftRevision } : {}),
              },
            }
          : {}),
        clientRequestId: crypto.randomUUID(),
      };
      sendMessageMutation.mutate(variables, {
        onSuccess: (data) => {
          if (data.turnId) setActiveTurnId(data.turnId);
        },
        onError: (err: unknown) => {
          removeLastUserMessage(content);
          const errData = (err as { data?: { code?: string; activeTurnId?: string } })?.data;
          if (errData?.code === 'TURN_IN_PROGRESS' && errData.activeTurnId) {
            setPrefillValue(content);
            setPrefillNonce((n) => n + 1);
            setActiveTurnId(errData.activeTurnId);
            return;
          }
          addMessage({
            type: 'error',
            message: err instanceof Error ? err.message : 'Something went wrong. Please try again.',
          });
          setAgentActive(false);
          setActiveTurnId(null);
        },
      });
    },
    [sessionId, recoveryStatus, turnStop.blocking, addMessage, removeLastUserMessage, sendMessageMutation],
  );

  const resetMutation = useMutation(orpc.audienceChat.reset.mutationOptions());

  const resetChat = useCallback(() => {
    if (!audienceId) return;
    resetMutation.mutate(
      { audienceId },
      {
        onSuccess: () => {
          setSessionId(null);
          clearMessages();
          initializedForSession.current = null;
          setAgentActive(false);
          setActiveTurnId(null);
          void refetchSession();
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to reset the conversation. Please try again.');
        },
      },
    );
  }, [audienceId, resetMutation, clearMessages, refetchSession]);

  const sessionReady = !!sessionId;

  return {
    sessionId,
    messages,
    connected,
    /** Demo workspace only: this thread hit its LLM budget and the server will refuse further turns. */
    budgetExhausted: sessionData?.session?.budgetExhausted ?? false,
    error: streamHistoryRecovered ? null : sseError,
    sendMessage,
    resetChat,
    recoveryStatus,
    retryRecovery,
    agentActive: agentActive || turnStop.blocking,
    stopping: turnStop.stopping || turnStop.pending,
    stopStatusError: turnStop.statusError,
    stopStalled: turnStop.stopStalled,
    retryStopStatus: () => {
      void turnStop.retryStatus();
    },
    sessionReady,
    stopTurn,
    canStop:
      (agentActive || turnStop.blocking) &&
      (knownTurnId !== null || turnStop.blockingTurnId !== null) &&
      !turnStop.terminalSeen &&
      !turnStop.stopping &&
      !turnStop.pending,
    prefillValue,
    prefillNonce,
    pendingBlock,
    resolveBlock,
    blockSubmitting: submittingBlockId === pendingBlock?.blockId,
    settlingAfterTurn,
    composerFocusNonce,
  };
}
